import { useNotifications } from '@/data/Content/Notifications';
import { useGetInterests } from 'integration/hooks/useGetInterests';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { useUserInterests } from 'integration/hooks/useUserInterests';
import valueIsNullOrUndef from 'integration/utils/typeAssertions/valueIsNullOrUndef';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const PREFERENCES_CODE = 'preferences';

export const useInterest = () => {
	const { showSnackbarMessage, notifyError } = useNotifications();
	const [submitting, setSubmitting] = useState(false);
	const [selectedUserInterests, setSelectedUserInterests] = useState<number[]>([]);
	const isInterestsInitialized = useRef(false);
	const openInterestsModal = useIsOpen();

	const interestsListQuery = useGetInterests({ code: PREFERENCES_CODE });

	const userInterestsHook = useUserInterests();

	const userInterestsData = useMemo(
		() =>
			userInterestsHook.userInterests?.find(
				(item) => item.name.toLocaleLowerCase().trim() === PREFERENCES_CODE
			),
		[userInterestsHook.userInterests]
	);

	const interestsListData = useMemo(
		() =>
			interestsListQuery.data?.find(
				(item) => item.name.toLocaleLowerCase().trim() === PREFERENCES_CODE
			),
		[interestsListQuery.data]
	);

	useEffect(() => {
		const userInterests = userInterestsData?.children || [];
		if (isInterestsInitialized.current || userInterests.length === 0) return;
		const interestsIds = userInterests.map((interest) => interest.id);
		setSelectedUserInterests(interestsIds);
		isInterestsInitialized.current = true;
	}, [userInterestsData]);

	const onUpdateUserInterests = useCallback(async () => {
		const parentId = interestsListData?.id;

		if (valueIsNullOrUndef(parentId)) {
			notifyError('Por favor intente nuevamente más tarde', 'Ocurrió un problema');
			return;
		}

		setSubmitting(true);
		try {
			await userInterestsHook.onUpdateUserInterests([
				{ parentId, children: selectedUserInterests },
			]);
			showSnackbarMessage('Intereses actualizados correctamente');
		} catch (error) {
			notifyError('Por favor intente nuevamente más tarde', 'Ocurrió un problema');
		} finally {
			setSubmitting(false);
			openInterestsModal.off();
		}
	}, [
		showSnackbarMessage,
		notifyError,
		interestsListData?.id,
		openInterestsModal,
		selectedUserInterests,
		userInterestsHook,
	]);

	const onToggleUserInterest = useCallback((interestId: number) => {
		setSelectedUserInterests((prev) => {
			if (prev.includes(interestId)) {
				return prev.filter((id) => id !== interestId);
			} else {
				return [...prev, interestId];
			}
		});
	}, []);

	return {
		interests: interestsListData?.children || [],
		userInterests: userInterestsData?.children || [],
		isSubmittingInterests: submitting,
		isLoadingInterests: interestsListQuery.isLoading,
		selectedUserInterests,
		openInterestsModal: openInterestsModal.open,
		onOpenInterestsModal: openInterestsModal.on,
		onCloseInterestsModal: openInterestsModal.off,
		onUpdateUserInterests,
		onToggleUserInterest,
	};
};
